import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { NameBuilderConfiguration, ErrorLevel, IValueViewModel, ObjectType, GetAggregatedValueDto, AggregationMode, Unit } from '../../models';
import { IIdentifierVmService, INameBuilderService, IUnitService, ISeriesService, IDateRangeService, IwidgetIntervalHelperService, IFormatNumberHelper } from '../../services';
import { WideError, missingSeries, popoverItem, getUnitName } from '../../common';
import { SeriesConfiguration, ChartIdentifierWidgetConfiguration } from '../../widgets';
import { SeriesMappingResult } from './SeriesMappingResult';
import { ISeriesBuilder, ISeriesBuilderArguments } from './ISeriesBuilder';
import { mapConfiguration } from '../../common/helpers/valueViewModelHelper';
import { getPopoverCallback } from '../../widgets/common/callback';
import { ChartByIdentifierWidget } from '../../widgets/widgets.index';

export interface IChartByIdentifierService extends ISeriesBuilder {
    getSeries(
        seriesConfig: SeriesConfiguration[],
        nameConfiguration: NameBuilderConfiguration,
        unitName?: string
    ): angular.IPromise<{ error: WideError; series: any[] }>;
    getRange(configuration: ChartIdentifierWidgetConfiguration): { beginMoment: moment.Moment; endMoment: moment.Moment };
    getConfiguration(item: ChartByIdentifierWidget): GetAggregatedValueDto;
    getConfigurationAsync(item: { configuration: ChartIdentifierWidgetConfiguration }): angular.IPromise<GetAggregatedValueDto>;
}

export class ChartByIdentifierService implements IChartByIdentifierService {
    static serviceId = 'chartByIdentifierService';

    static $inject = [
        '$q',
        '$state',
        'identifierVmService',
        'nameBuilderService',
        'unitService',
        'seriesService',
        'dateRangeService',
        'widgetIntervalHelperService',
        'formatNumberHelper'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$state: angular.ui.IStateService,
        private _identifierVmService: IIdentifierVmService,
        private _nameBuilderService: INameBuilderService,
        private _unitService: IUnitService,
        private _seriesService: ISeriesService,
        private _dateRangeService: IDateRangeService,
        private _intervalHelper: IwidgetIntervalHelperService,
        private _formatNumberHelper: IFormatNumberHelper) {
    }
    
    getSeries(seriesConfig: SeriesConfiguration[],
        nameConfiguration: NameBuilderConfiguration,
        unitName: string = null): angular.IPromise<{ error: WideError, series: any[] }> {
        return this._identifierVmService.getViewModelsFromApiFromGuidList(seriesConfig.map(s => s.guid))
            .then(vms => {
                const dic = {};
                let error: WideError;

                vms.forEach(vm => dic[vm.guid] = vm);

                if (_.some(seriesConfig, s => !dic[s.guid])) {
                    error = new WideError(ErrorLevel.Error, missingSeries);
                }

                return {
                    error: error,
                    series: _.sortBy(seriesConfig.filter(s => dic[s.guid])
                        .map(s => this.mapSerieFromConfiguration(dic[s.guid], s, nameConfiguration, unitName)), s => (s as any).sortOrder)
                };
            });
    }

    getRange(configuration: ChartIdentifierWidgetConfiguration) {
        const beginDate = this._dateRangeService.beginDate() as string;
        const endDate = this._dateRangeService.endDate() as string;
        const updatedInterval = this._intervalHelper.getInterval(beginDate, endDate, configuration);
        return updatedInterval;
    }

    getConfiguration(item: ChartByIdentifierWidget): GetAggregatedValueDto {
        const range = this.getRange(item.configuration);
        const configuration: GetAggregatedValueDto = {
            beginDateTime: range.beginMoment.toISOString(),
            endDateTime: range.endMoment.toISOString(),
            aggregationMode: item.configuration.aggregation
                ? item.configuration.aggregation.mode
                : AggregationMode.None,
            hasQuality: false,
            list: item.configuration.series.map(d => {
                return {
                    guid: d.guid,
                    value: d.value ? d.value : 'average'
                };
            })
        };
        return configuration;
    }

    getConfigurationAsync(item: { configuration: ChartIdentifierWidgetConfiguration }): angular.
        IPromise<GetAggregatedValueDto> {
        return this._seriesService.getUpdatedGuids(item.configuration.series)
            .then(data => {
                const range = this.getRange(item.configuration);
                const configuration: GetAggregatedValueDto = {
                    beginDateTime: range.beginMoment.toISOString(),
                    endDateTime: range.endMoment.toISOString(),
                    aggregationMode: item.configuration.aggregation
                        ? item.configuration.aggregation.mode
                        : AggregationMode.None,
                    hasQuality: false,
                    list: data.map(d => {
                        return {
                            guid: d.guid,
                            value: d.value ? d.value : 'average'
                        };
                    })
                };
                return configuration;
            });
    }

    buildSeriesFromViewModels(args: ISeriesBuilderArguments): angular.IPromise<SeriesMappingResult> {
        const { configuration, viewModels, map, errors } = args;
        const promise = configuration.options.variableTypeGuid && configuration.options.unitGuid
            ? this._unitService.getByGuid(configuration.options.unitGuid)
            : null;
        return this._$q
            .when(promise)
            .then(unit => {
                const seriesConfig: any[] = (configuration as any).series;
                const nameConfiguration = configuration.nameConfiguration;
                const mapped = mapConfiguration(seriesConfig, viewModels, map, errors);
                const series = mapped.series
                    .filter(s => !s.missing)
                    .map(s => {
                        const unitName = this.getFinalUnit(s.viewModel, unit, configuration);
                        return this.mapSerieFromConfiguration(s.viewModel, s.configuration, nameConfiguration, unitName);
                    });
                return {
                    error: mapped.error,
                    series: _.sortBy(series, s => s.sortOrder),
                    map
                };
            });
    }

    private getFinalUnit(vm: IValueViewModel, unit: Unit, configuration: any): string {
        const serieUnitName = (unit && unit.guid !== vm.guid)
            ? unit.name
            : (unit && configuration.options.variableTypeGuid === vm.variableTypeGuid)
                ? vm.unitName
                : null;
        return getUnitName(serieUnitName, configuration.valueType);
    }

    private mapSerieFromConfiguration(
        serie: IValueViewModel,
        seriesConfiguration: any,
        nameConfiguration?: NameBuilderConfiguration,
        unitName: string = null) {

        const item = angular.copy(serie);
        const output = angular.copy(serie) as any;
        output.displayMax = seriesConfiguration.displayMax;
        output.displayMin = seriesConfiguration.displayMin;
        output.sortOrder = seriesConfiguration.sortOrder;
        output.hasMin = this.hasMinMax(serie.min);
        output.hasMax = this.hasMinMax(serie.max);
        const replacementUnit = getUnitName(serie.unitName, seriesConfiguration.value);
        if (replacementUnit) {
            output.unitName = replacementUnit;
        }
        if (unitName) {
            output.unitName = unitName;
        }

        output.decimals = seriesConfiguration.decimals === undefined
            || seriesConfiguration.decimals === null
            || seriesConfiguration.decimals < 0
            ? 2
            : seriesConfiguration.decimals;
        output.format = this._formatNumberHelper.getFormatter(output.decimals).format;
        // to trigger digest when changing language
        output.lang = moment.locale();
        output.builtName = this._nameBuilderService.getName(output, nameConfiguration, seriesConfiguration);
        output.clickCallback = getPopoverCallback(serie, this._$state.go);
        output.attributes = {
            'wt-popover-svg': true,
            template: serie.objectType === ObjectType.Tag
                ? 'tagPopover.html'
                : 'identifierPopover.html',
            item: popoverItem(item),
            'compile': 'wt-popover-svg'
        };
        output.valueType = seriesConfiguration.value;
        output.identifier = seriesConfiguration.value + output.guid;
        output.stepMode = seriesConfiguration.stepMode;
        return output;
    }

    private hasMinMax(value) {
        return value !== undefined && value !== null;
    }
}